import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormInput, Button, LoadingSpinner, Pagination } from 'components/common';
import { useSelector, useDispatch } from 'react-redux';
import { FiSearch, FiPlus, FiHelpCircle } from 'react-icons/fi';
import {
  getFaqs,
  setPageSize,
  setCurrentPage,
  setCurrentParams
} from 'redux/documentation/faqSlice';
import { debounce } from 'helpers/debounce';
import FaqDeleteConfirmation from '../../components/app/documentation/faq/FaqDeleteConfirmation';
import FaqItem from '../../components/app/documentation/faq/FaqItem';
import FaqForm from '../../components/app/documentation/faq/FaqForm';

const FaqPage = () => {
  const [filterValue, setFilterValue] = useState('');
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formTitle, setFormTitle] = useState(null);
  const [selectedFaq, setSelectedFaq] = useState(null);

  const { paginate, currentParams, sortParams, data, loading, totalRows } = useSelector(
    state => state.documentation_faq
  );
  const { permissions } = useSelector(state => state.profile_user.data);
  const { v305 } = permissions || {};

  const dispatch = useDispatch();
  const perPageRef = useRef(paginate.pageSize);

  useEffect(() => {
    dispatch(getFaqs({ ...currentParams, sort: sortParams }));
  }, [dispatch, currentParams, sortParams]);

  const handleMakeFaq = () => {
    setShowFaqForm(true);
    setFormTitle('New FAQ');
    setSelectedFaq(null);
  };

  const handleChangeDebounced = useMemo(
    () => debounce((value) => {
      const newParams = { ...currentParams, title_cont: value, page: 1 };
      dispatch(setCurrentParams(newParams));
    }, 500),
    [dispatch, currentParams]
  );

  const handleChange = (e) => {
    const { value } = e.target;
    setFilterValue(value);
    handleChangeDebounced(value);
  };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
    dispatch(setCurrentParams({ ...currentParams, page, per_page: perPageRef.current }));
  };

  const handlePageSizeChange = (size) => {
    perPageRef.current = size;
    dispatch(setPageSize(size));
    dispatch(setCurrentParams({ ...currentParams, page: 1, per_page: size }));
  };

  const handleEditFaq = (faq) => {
    setShowFaqForm(true);
    setFormTitle('Edit FAQ');
    setSelectedFaq(faq);
  };

  const handleDeleteFaq = (faq) => {
    setShowDeleteConfirmation(true);
    setSelectedFaq(faq);
  };

  return (
    <>
      {/* Page Header */}
      <div className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-10 tw-h-10 tw-rounded tw-bg-blue-50 tw-border tw-border-blue-100 tw-flex tw-items-center tw-justify-center tw-text-blue-600">
            <FiHelpCircle size={20} />
          </div>
          <div>
            <h1 className="tw-text-2xl tw-font-bold tw-text-slate-900">Frequently Asked Questions</h1>
            <p className="tw-text-sm tw-text-slate-500 tw-mt-0.5">Find answers to common questions about Pebsteel Portal</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="tw-mb-7 tw-flex tw-items-center tw-justify-between tw-gap-4 tw-flex-wrap">
        <div className="tw-flex-1 tw-min-w-[250px] tw-max-w-md">
          <FormInput
            type="text"
            placeholder="Search FAQs..."
            value={filterValue}
            onChange={handleChange}
            icon={<FiSearch size={18} />}
          />
        </div>
        {v305 && (
          <Button 
            variant="primary" 
            onClick={handleMakeFaq}
            className="tw-flex tw-items-center tw-gap-2 tw-shadow-md hover:tw-shadow-lg tw-transition-all"
          >
            <FiPlus size={18} /> New FAQ
          </Button>
        )}
      </div>

      {/* FAQ List */}
      {loading ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
          <LoadingSpinner size="md" message="Loading FAQs..." />
        </div>
      ) : data.length === 0 ? (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-text-center tw-py-20 tw-px-4">
          <div className="tw-w-16 tw-h-16 tw-bg-slate-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
            <FiHelpCircle size={28} className="tw-text-slate-300" />
          </div>
          <p className="tw-text-slate-600 tw-text-base tw-font-semibold">No FAQs found</p>
          <p className="tw-text-slate-400 tw-text-sm tw-mt-1">Try adjusting your search or create a new FAQ</p>
        </div>
      ) : (
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-divide-y tw-divide-slate-100 tw-overflow-hidden">
          {data.map(faq => (
            <FaqItem
              key={faq.id}
              faq={faq}
              editFaq={() => handleEditFaq(faq)}
              deleteFaq={() => handleDeleteFaq(faq)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && data.length > 0 && (
        <div className="tw-mt-4 tw-bg-white tw-rounded-lg tw-shadow-sm tw-px-6 tw-py-4">
          <Pagination
            currentPage={paginate.currentPage || 1}
            totalPages={paginate.totalPagesCount || 1}
            totalResults={totalRows}
            perPage={paginate.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {/* Modals */}
      {showFaqForm && (
        <FaqForm
          showModal={showFaqForm}
          setshowModal={setShowFaqForm}
          title={formTitle}
          faq={selectedFaq}
          onSaved={() => dispatch(getFaqs({ ...currentParams, sort: sortParams }))}
        />
      )}

      {showDeleteConfirmation && (
        <FaqDeleteConfirmation
          showModal={showDeleteConfirmation}
          setshowModal={setShowDeleteConfirmation}
          faq={selectedFaq}
        />
      )}
    </>
  );
};

export default FaqPage;
